import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import ThemeShell from './ThemeShell.jsx';
import ProtectedRoute from './ProtectedRoute.jsx';
import AppErrorBoundary from 'components/AppErrorBoundary.jsx';
import { Spinner } from 'components/common';
import LoginPage from 'views/login';
import { DEFAULT_ROUTE } from 'components/layout/navigation.js';

/*
 * One chunk per page. The app shipped as a single 635 KB bundle, so the first
 * visit downloaded every page's code — charts, the archive, settings — to
 * show one of them. Each page now arrives when first needed, and once the
 * first screen is up the rest are fetched in the background (see
 * usePrefetchPages), so switching tabs never waits on the network.
 */
const PAGES = {
  overview: () => import('views/overview'),
  campaigns: () => import('views/campaigns'),
  analytics: () => import('views/analytics'),
  tests: () => import('views/tests'),
  payouts: () => import('views/payouts'),
  affiliates: () => import('views/affiliates'),
  archive: () => import('views/archive'),
  custom: () => import('views/custom'),
  activity: () => import('views/activity'),
  notifications: () => import('views/notifications'),
  settings: () => import('views/settings'),
};
const OverviewPage = lazy(PAGES.overview);
const CampaignsPage = lazy(PAGES.campaigns);
const AnalyticsPage = lazy(PAGES.analytics);
const TestsPage = lazy(PAGES.tests);
const PayoutsPage = lazy(PAGES.payouts);
const AffiliatesPage = lazy(PAGES.affiliates);
const ArchivePage = lazy(PAGES.archive);
const CustomPage = lazy(PAGES.custom);
const ActivityPage = lazy(PAGES.activity);
const NotificationsPage = lazy(PAGES.notifications);
const SettingsPage = lazy(PAGES.settings);

/** Warm every page chunk once the browser is idle after the first screen. */
function usePrefetchPages() {
  useEffect(() => {
    const warm = () => { for (const load of Object.values(PAGES)) load().catch(() => {}); };
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(warm, { timeout: 4000 })
      : window.setTimeout(warm, 2000);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(idle) : window.clearTimeout(idle));
  }, []);
}

/**
 * The whole URL surface, in one file.
 *
 * Routes are THEME-AGNOSTIC on purpose: /campaigns is /campaigns in all three
 * interfaces, so switching theme keeps you on the page you were reading.
 *
 * Where a view differs by theme it branches ONCE, at the top of that view, on
 * useTheme() — never in its leaf components. ActivityPage is the example: one
 * isV3 check picks a header, and the stream underneath knows nothing about
 * themes.
 *
 * Each view gets its own error boundary, so a throw in Campaigns leaves the
 * shell and the nav intact instead of blanking the app. public/index.html did
 * this only for V3, and not at all for V1/V2.
 *
 * Order here matches the nav (components/layout/navigation.js), which is the
 * order these appear to a user in both the tab bar and the V3 rail.
 */
const VIEWS = [
  { path: '/overview',  title: 'Overview',     element: <OverviewPage /> },
  { path: '/campaigns', title: 'Campaigns',    element: <CampaignsPage /> },
  { path: '/analytics', title: 'Analytics',    element: <AnalyticsPage /> },
  { path: '/tests',     title: 'Tests',        element: <TestsPage /> },
  { path: '/payouts',   title: 'Payouts',      element: <PayoutsPage /> },
  { path: '/affiliates', title: 'Affiliates',   element: <AffiliatesPage /> },
  { path: '/archive',   title: 'Archive',      element: <ArchivePage /> },
  { path: '/custom',    title: 'Custom',       element: <CustomPage /> },
  { path: '/activity',  title: 'Activity Log', element: <ActivityPage /> },
  { path: '/notifications', title: 'Notifications', element: <NotificationsPage /> },
  { path: '/settings',  title: 'Settings',     element: <SettingsPage /> },
];

export default function AppRoutes() {
  usePrefetchPages();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute><ThemeShell /></ProtectedRoute>}>
        {VIEWS.map(v => (
          <Route
            key={v.path}
            path={v.path}
            element={<AppErrorBoundary label={v.title}><Suspense fallback={<Spinner />}>{v.element}</Suspense></AppErrorBoundary>}
          />
        ))}
        <Route index element={<Navigate to={DEFAULT_ROUTE} replace />} />
      </Route>

      {/* Unknown URL — send people somewhere real rather than a blank shell. */}
      <Route path="*" element={<Navigate to={DEFAULT_ROUTE} replace />} />
    </Routes>
  );
}
